import { Button } from '@/shared/ui/button';
import { FiMessageSquare, FiPlus } from 'react-icons/fi';
import { cn } from '@/shared/lib';
import {
  useAssistantThreads,
  useSelectedAssistantThreadId,
} from '@/modules/assistant';
import type { AssistantScope } from '@/modules/assistant';
import type { AssistantThread } from '@/api/schemas/assistant';

export type ChatSidebarProps = {
  /** Opens a conversation. */
  onSelect: (threadId: string) => void;
  /** Starts a new conversation. */
  onNewConversation: () => void;
};

/**
 * The conversation history sidebar.
 *
 * Fetches the caller's threads itself (React Query dedupes with any other
 * caller), so the layout does not have to thread the data through.
 */
export function ChatSidebar({ onSelect, onNewConversation }: ChatSidebarProps) {
  const scope: AssistantScope = 'platform';
  const threads = useAssistantThreads(scope);
  const threadId = useSelectedAssistantThreadId(scope);

  const resolvedThreads = threads.data ?? [];

  return (
    <aside className="border-r border-border bg-surface flex flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-border bg-surface/50 px-3 py-2">
        <h2 className="font-semibold text-content">Conversations</h2>
        <Button size="sm" variant="ghost" onClick={onNewConversation}>
          <FiPlus className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          New conversation
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-2" aria-label="Conversations">
        {resolvedThreads.length === 0 ? (
          <p className="text-sm text-content-muted">No conversations yet.</p>
        ) : null}

        {resolvedThreads.map((thread: AssistantThread) => {
          const isSelected = thread.id === threadId;
          return (
            <button
              key={thread.id}
              type="button"
              aria-current={isSelected ? 'true' : undefined}
              onClick={() => onSelect(thread.id)}
              className={cn(
                'flex w-full items-center gap-2 rounded-control border border-transparent px-2 py-1.5 text-left transition-colors hover:border-border hover:bg-surface',
                isSelected && 'border-border bg-surface',
              )}
            >
              <div className="flex shrink-0 items-center justify-center rounded-control bg-surface-sunken text-accent p-1">
                <FiMessageSquare className="h-3 w-3" aria-hidden="true" />
              </div>
              <div className="min-w-0 flex-1">
                <span className="truncate block font-medium text-content">
                  {thread.title}
                </span>
                <span className="truncate block text-2xs text-content-subtle">
                  {thread.messageCount} messages · updated recently
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </aside>
  );
}
